import React from 'react';
import { useQuery } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { INSTALLABLE_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import { UIActions } from '../ui/uiSlice';
import Link from '../utils/router/Link';
import FixedBottomBanner from './FixedBottomBanner';

const BASE_INTEGRATION_BANNER_INTEGRATION_FRAGMENT = gql(`
  fragment BaseIntegrationBanner_integration on Integration {
    id
    platform
    status
  }
`);

interface BaseIntegrationBannerProps {
  integration: DocumentType<
    typeof BASE_INTEGRATION_BANNER_INTEGRATION_FRAGMENT
  >;
  onDismiss: () => void;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseIntegrationBanner = ({
  integration,
  onDismiss,
}: BaseIntegrationBannerProps) => {
  const { status, platform } = integration;
  const { name } = INSTALLABLE_PLATFORM_INFO_MAP[platform] ?? {};

  return (
    <FixedBottomBanner
      onDismiss={onDismiss}
      variant={status === 'ERROR' ? 'danger' : 'inverted'}
    >
      {status === 'ERROR' ? (
        <>
          Your {name} integration is not working as expected.{' '}
          <Link to="/admin/integrations">Please reinstall it here</Link>.
        </>
      ) : (
        <>
          An update is available for your {name} integration.{' '}
          <Link to="/admin/integrations">Get the latest here</Link>.
        </>
      )}
    </FixedBottomBanner>
  );
};

const INTEGRATIONS_QUERY = gql(/** @complexity 4 */ `
  query IntegrationsBannerIntegrations {
    session: currentSession {
      organization {
        id
        integrations {
          ...BaseIntegrationBanner_integration
        }
      }
    }
  }
`);

const useAbNormalIntegration = () => {
  const userType = useAppSelector((state) => state.session.user.type);
  const isAdmin = userType === 'ADMIN';

  const isIntegrationBannerHidden = useAppSelector(
    (state) => state.ui.isIntegrationBannerHidden,
  );

  const { data } = useQuery(INTEGRATIONS_QUERY, {
    skip: !isAdmin || isIntegrationBannerHidden,
  });

  if (!data || !isAdmin || isIntegrationBannerHidden) {
    return null;
  }

  return data.session.organization.integrations.find(
    (i) => i.status && i.status !== 'NORMAL',
  );
};

const IntegrationBanner = () => {
  const dispatch = useAppDispatch();
  const integration = useAbNormalIntegration();

  if (!integration) return null;

  return (
    <BaseIntegrationBanner
      onDismiss={() => dispatch(UIActions.hideIntegrationBanner())}
      integration={integration}
    />
  );
};

IntegrationBanner.useIsActive = () => !!useAbNormalIntegration();

export default IntegrationBanner;
